export type RGB = [number, number, number];

export interface Palette {
  dark: boolean;
  dust: RGB;
  mid: RGB;
  ink: RGB;
  gold: RGB;
  green: RGB;
  red: RGB;
  dustA: number;
  midA: number;
}

export interface Point {
  x: number;
  y: number;
  z?: number;
  r: number;
  c: RGB;
  a: number;
  blur?: number;
  seg?: [number, number];
}

/** Screen anchor for an HTML result label: `[x, y, z, opacity?]`. */
export type Anchor = [number, number, number, (number | string)?];

export type Projector = (
  x: number,
  y: number,
  z: number,
) => [number, number, number];

export interface Frame {
  W: number;
  H: number;
  cx: number;
  cy: number;
  R: number;

  rs: number;
  t: number;
  pal: Palette;

  hover: number;
  resultCount: number;
  scrub: number;
  hash: (a: number, b: number) => number;
  projector: (
    yaw: number,
    pitch: number,
    cx: number,
    cy: number,
    s: number,
  ) => Projector;
  basis: (nx: number, ny: number, nz: number) => number[];
  fib: (i: number, n: number) => [number, number, number];
  lerp: (a: number, b: number, t: number) => number;
  clamp: (v: number, a: number, b: number) => number;
}

export interface RenderOutput {
  pts: Point[];
  sats?: Anchor[];
}

export type RenderFn = (g: Frame) => RenderOutput;

export interface PointFieldConfig {
  container: HTMLElement;
  canvas: HTMLCanvasElement;
  frontCanvas?: HTMLCanvasElement | null;
  render: RenderFn;
  onAnchors?: (anchors: Anchor[]) => void;
  satMinR?: number;
  resultCount?: number;
  freezeOnHover?: boolean;
  scrub0?: number;
}

export interface PointFieldHandle {
  destroy(): void;
  suspend(value: boolean): void;
  setPaused(value: boolean): void;
  isPaused(): boolean;
  setHover(index: number): void;
  setScrub(value: number): void;
  refresh(): void;
}

/* ============================================================
   Maths — shared with the reference orbit renderer. Keep intact.
   ============================================================ */

export function hash(a: number, b: number): number {
  const n = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

export function projector(
  yaw: number,
  pitch: number,
  cx: number,
  cy: number,
  s: number,
): Projector {
  const sp = Math.sin(pitch);
  const cp = Math.cos(pitch);
  const sy = Math.sin(yaw);
  const cw = Math.cos(yaw);
  return (x, y, z) => {
    const px = x * cw + z * sy;
    const pz = -x * sy + z * cw;
    const py = y * cp - pz * sp;
    const w = y * sp + pz * cp; // w = depth
    return [cx + px * s, cy - py * s, w];
  };
}

export function basis(nx: number, ny: number, nz: number): number[] {
  let ux = -ny;
  let uy = nx;
  const uz = 0;
  const L = Math.max(1e-6, Math.hypot(ux, uy));
  ux /= L;
  uy /= L;
  return [ux, uy, uz, ny * uz - nz * uy, nz * ux - nx * uz, nx * uy - ny * ux];
}

export function fib(i: number, n: number): [number, number, number] {
  const ga = Math.PI * (3 - Math.sqrt(5));
  const y = 1 - (2 * (i + 0.5)) / n;
  const r = Math.sqrt(Math.max(0, 1 - y * y));
  const th = i * ga;
  return [r * Math.cos(th), y, r * Math.sin(th)];
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(v: number, a: number, b: number): number {
  if (v < a) return a;
  if (v > b) return b;
  return v;
}

export function palette(): Palette {
  const dark = document.documentElement.dataset.mode === "dark";
  return dark
    ? {
        dark: true,
        dust: [236, 232, 224],
        mid: [206, 200, 188],
        ink: [247, 244, 238],
        gold: [214, 180, 110],
        green: [143, 196, 107],
        red: [224, 122, 107],
        dustA: 1.0,
        midA: 1.0,
      }
    : {
        dark: false,
        dust: [95, 89, 80],
        mid: [118, 111, 100],
        ink: [28, 26, 23],
        gold: [165, 129, 60],
        green: [86, 132, 58],
        red: [176, 64, 48],
        dustA: 1.55,
        midA: 1.7,
      };
}

export function prefersReducedMotion(): boolean {
  return (
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function sortByDepth(pts: Point[]): void {
  if (pts.length && pts[0].z !== undefined) {
    pts.sort((A, B) => (A.z ?? 0) - (B.z ?? 0));
  }
}

/* ============================================================
   Controller
   ============================================================ */

export function createPointField(cfg: PointFieldConfig): PointFieldHandle {
  const ctx = cfg.canvas.getContext("2d");
  const ctxF = cfg.frontCanvas ? cfg.frontCanvas.getContext("2d") : null;
  if (!ctx) throw new Error("pointField: 2D context unavailable");

  let W = 0;
  let H = 0;
  let DPR = 1;
  let raf = 0;
  let paused = false;
  let hover = -1;
  let scrub = cfg.scrub0 ?? 0.5;
  let t = 0;
  let t0 = performance.now() / 1000;
  let destroyed = false;

  const freezeOnHover = cfg.freezeOnHover !== false;
  const satMinR = cfg.satMinR ?? 0;
  const resultCount = cfg.resultCount ?? 0;

  function resize(): void {
    const b = cfg.container.getBoundingClientRect();
    DPR = Math.min(2, window.devicePixelRatio || 1);
    W = b.width;
    H = b.height;
    cfg.canvas.width = Math.round(W * DPR);
    cfg.canvas.height = Math.round(H * DPR);
    if (cfg.frontCanvas) {
      cfg.frontCanvas.width = Math.round(W * DPR);
      cfg.frontCanvas.height = Math.round(H * DPR);
    }
  }

  /**
   * Sort far→near once, then dispatch each point to one of two stacked canvases.
   *
   * Painting order is preserved within each canvas, so depth ordering survives
   * the split. The nucleus sits between them in the z-index stack, which is what
   * makes an orbit read as a path AROUND a body rather than an ellipse on glass.
   */
  function paint(pts: Point[]): void {
    sortByDepth(pts);
    ctx!.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx!.clearRect(0, 0, W, H);
    if (ctxF) {
      ctxF.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctxF.clearRect(0, 0, W, H);
    }

    for (const p of pts) {
      if (!Number.isFinite(p.a) || p.a < 0.02) continue;
      // z > 0 = nearer the viewer than the nucleus centre -> front canvas
      const target = ctxF && p.z !== undefined && p.z > 0 ? ctxF : ctx!;
      const rgba = `rgba(${p.c[0]},${p.c[1]},${p.c[2]},${p.a.toFixed(3)})`;

      if (p.blur) {
        target.shadowColor = rgba;
        target.shadowBlur = p.blur;
      } else {
        target.shadowBlur = 0;
      }

      if (p.seg) {
        target.strokeStyle = rgba;
        target.lineWidth = p.r;
        target.lineCap = "round";
        target.beginPath();
        target.moveTo(p.x, p.y);
        target.lineTo(p.seg[0], p.seg[1]);
        target.stroke();
      } else {
        target.fillStyle = rgba;
        target.beginPath();
        target.arc(p.x, p.y, Math.max(0.3, p.r), 0, 6.2832);
        target.fill();
      }
    }

    // Reset both contexts, not just the last one touched. The original reset
    // whichever context the final point happened to use, which left a stale
    // shadowBlur on the other canvas whenever the last point was blurred.
    ctx!.shadowBlur = 0;
    if (ctxF) ctxF.shadowBlur = 0;
  }

  function frame(): Frame {
    return {
      W,
      H,
      cx: W / 2,
      cy: H / 2,
      R: (Math.min(W, H) / 2) * 0.82,
      rs: Math.pow(Math.min(W, H) / 300, 0.6),
      t,
      pal: palette(),
      hover,
      resultCount,
      scrub,
      hash,
      projector,
      basis,
      fib,
      lerp,
      clamp,
    };
  }

  /** Apply the label stand-off, then hand the anchors up to React. */
  function emitAnchors(anchors: Anchor[], cx: number, cy: number): void {
    if (!cfg.onAnchors) return;
    if (!satMinR) {
      cfg.onAnchors(anchors);
      return;
    }
    const adjusted = anchors.map((A): Anchor => {
      let lx = A[0] - cx;
      let ly = A[1] - cy;
      const ld = Math.hypot(lx, ly);
      if (ld < satMinR) {
        const k = ld < 1 ? 1 : satMinR / ld;
        lx *= k;
        ly *= k;
      }
      return [cx + lx, cy + ly, A[2], A[3]];
    });
    cfg.onAnchors(adjusted);
  }

  function loop(): void {
    if (destroyed) return;
    if (!paused && (hover === -1 || !freezeOnHover)) {
      t = performance.now() / 1000 - t0;
    }
    const g = frame();
    const out = cfg.render(g) || { pts: [] };
    paint(out.pts || []);
    emitAnchors(out.sats ?? [], g.cx, g.cy);
    raf = requestAnimationFrame(loop);
  }

  /** One static frame, for `prefers-reduced-motion`. */
  function paintStatic(): void {
    t = 6;
    const g = frame();
    const out = cfg.render(g) || { pts: [] };
    paint(out.pts || []);
    emitAnchors(out.sats ?? [], g.cx, g.cy);
  }

  const onWindowResize = (): void => {
    resize();
    if (!raf) paintStatic();
  };

  resize();
  window.addEventListener("resize", onWindowResize);

  const reduced = prefersReducedMotion();
  if (reduced) paintStatic();
  else loop();

  return {
    destroy(): void {
      destroyed = true;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      window.removeEventListener("resize", onWindowResize);
    },
    suspend(value: boolean): void {
      if (destroyed || reduced) return;
      if (value) {
        if (raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
      } else if (!raf) {
        resize();
        t0 = performance.now() / 1000 - t;
        loop();
      }
    },
    setPaused(value: boolean): void {
      paused = value;
      if (!paused) t0 = performance.now() / 1000 - t;
    },
    isPaused: () => paused,
    setHover(index: number): void {
      hover = index;
    },
    setScrub(value: number): void {
      scrub = value;
    },
    refresh(): void {
      resize();
      if (!raf) paintStatic();
    },
  };
}
